import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import Card from '../components/Card';
import { CardSkeleton } from '../components/LoadingSkeleton';

const ChangelogPage: React.FC = () => {
  const [changelogContent, setChangelogContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChangelog = async () => {
      try {
        // Fetch CHANGELOG.md from the public directory or root
        const response = await fetch('/CHANGELOG.md');
        if (!response.ok) {
          throw new Error('Changelog not available');
        }
        const text = await response.text();
        setChangelogContent(text);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load changelog';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadChangelog();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
        </div>
        <CardSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card variant="elevated">
          <Card.Header title="Changelog Not Available" />
          <Card.Body>
            <p className="text-gray-900 dark:text-gray-100 mb-4">{error}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Please check the{' '}
              <a
                href="https://github.com/AkhileDigital/project-management-frontend/blob/main/CHANGELOG.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                repository
              </a>{' '}
              for version history.
            </p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Card variant="elevated">
        <Card.Header 
          title="Changelog" 
          subtitle="Version history and release notes"
        />
        <Card.Body>
          <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
            <ReactMarkdown
              components={{
                // Style h2 (version headings) with custom classes
                h2: ({ node, children, ...props }) => (
                  <h2
                    className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4 pb-2 border-b-2 border-primary dark:border-primary-400"
                    {...props}
                  >
                    {children}
                  </h2>
                ),
                // Style h3 (section headings: Added, Fixed, etc.) with custom classes
                h3: ({ node, children, ...props }) => (
                  <h3
                    className="text-xl font-semibold text-gray-900 dark:text-gray-100 mt-6 mb-3"
                    {...props}
                  >
                    {children}
                  </h3>
                ),
                // Style paragraphs
                p: ({ node, ...props }) => (
                  <p className="text-gray-900 dark:text-gray-100 mb-4" {...props} />
                ),
                // Style links
                a: ({ node, children, ...props }) => (
                  <a
                    className="text-primary-600 dark:text-primary-400 hover:text-accent-600 dark:hover:text-accent-400 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {children}
                  </a>
                ),
                // Style list items
                li: ({ node, ...props }) => (
                  <li className="text-gray-900 dark:text-gray-100 mb-2" {...props} />
                ),
                // Style unordered lists
                ul: ({ node, ...props }) => (
                  <ul className="list-disc list-inside text-gray-900 dark:text-gray-100 mb-4" {...props} />
                ),
                // Style code blocks
                code: ({ node, ...props }) => (
                  <code
                    className="bg-gray-100 dark:bg-gray-800 text-red-600 dark:text-red-400 px-2 py-1 rounded text-sm"
                    {...props}
                  />
                ),
                // Style strong/bold text
                strong: ({ node, ...props }) => (
                  <strong className="font-bold text-gray-900 dark:text-white" {...props} />
                ),
              }}
            >
              {changelogContent}
            </ReactMarkdown>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ChangelogPage;
